import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  normalizeDefinition, buildSteps, buildPeople, chapterCounts, hasContent, valueHasContent, questionVisible, findQuestion,
} from "@/villa142/config";
import {
  fetchProject, fetchQuestionnaire, fetchBrand, fetchWelcomeMedia, savePrefs, submitReflections, getToken, clearToken,
  initQueue, queueEntrySave, flushNow, clearPending, pendingQuestionKeys, hasPendingFor, hasPending, deleteEntry, setEntryRevs, setEntryRev,
} from "@/villa142/api";
import {
  Gate, Welcome, ContributorChoice, ChapterPause, ChapterEnd, PausedScreen, EndingScreen,
  SavedIndicator, ResponderPicker,
} from "@/villa142/screens";
import { QuestionCard } from "@/villa142/QuestionCard";
import { FlowNav } from "@/villa142/FlowNav";
import ReflectionMap from "@/villa142/ReflectionMap";
import Review from "@/villa142/Review";
import "@/villa142/villa142.css";

const LS_RESPONDER = "villa142_responder";
const LS_LAST_QID = "villa142_last_qid";
const LS_RETURN_CTX = "villa142_return_ctx";

// The questionnaire owns the document scroll root. Reset it directly (scrollTop, not just
// window.scrollTo) and defensively clear any questionnaire container that may have scrolled.
const resetScrollOwner = () => {
  const root = document.scrollingElement || document.documentElement;
  if (root) root.scrollTop = 0;
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
  const main = document.querySelector(".v142-main");
  if (main && main.scrollTop) main.scrollTop = 0;
  window.scrollTo(0, 0);
};
const scrollOwnerTop = () => (document.scrollingElement || document.documentElement).scrollTop || 0;

const blurActive = () => {
  const el = document.activeElement;
  if (el && el !== document.body && typeof el.blur === "function") el.blur();
};

const omitKey = (obj, key) => {
  const next = { ...obj };
  delete next[key];
  return next;
};

export default function Villa142Questionnaire() {
  const [phase, setPhase] = useState("checking");
  const [mode, setMode] = useState("flow"); // flow | paused | review | thanks
  const [def, setDef] = useState(null);
  const [people, setPeople] = useState(null);
  const [step, setStep] = useState(0);
  const [responder, setResponder] = useState(null);
  const [qActive, setQActive] = useState({});
  const [entriesMap, setEntriesMap] = useState({});
  const [conflicts, setConflicts] = useState({});
  const [saveState, setSaveState] = useState("idle");
  const [returnToReview, setReturnToReview] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [updatedAfterShareAt, setUpdatedAfterShareAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [navError, setNavError] = useState("");
  const [navBusy, setNavBusy] = useState(false);
  const navLockRef = useRef(false);
  const headerRef = useRef(null);
  const [brand, setBrand] = useState(null);
  const [welcomeMedia, setWelcomeMedia] = useState(null);
  const [endingMedia, setEndingMedia] = useState(null);
  const savedRef = useRef(new Set());
  const entriesRef = useRef({});
  const [navSeq, setNavSeq] = useState(0);

  // Own scroll restoration for the flow so Safari does not re-apply an old position.
  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return undefined;
    const prev = window.history.scrollRestoration;
    try { window.history.scrollRestoration = "manual"; } catch { /* ignore */ }
    return () => { try { window.history.scrollRestoration = prev; } catch { /* ignore */ } };
  }, []);

  // Route shell: hand the whole canvas (html/body/.App) to the questionnaire while mounted and
  // publish the measured visible viewport height for the full-screen stages (see villa142.css).
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add("v142-route");
    const vv = window.visualViewport;
    const publish = () => {
      const h = Math.max(vv ? vv.height : 0, window.innerHeight || 0);
      if (h > 0) {
        html.style.setProperty("--v142-vvh", `${Math.round(h)}px`);
        html.classList.add("v142-vv");
      }
    };
    publish();
    vv?.addEventListener("resize", publish);
    window.addEventListener("resize", publish);
    window.addEventListener("orientationchange", publish);
    return () => {
      vv?.removeEventListener("resize", publish);
      window.removeEventListener("resize", publish);
      window.removeEventListener("orientationchange", publish);
      html.classList.remove("v142-route", "v142-vv");
      html.style.removeProperty("--v142-vvh");
      html.removeAttribute("data-v142-stage");
    };
  }, []);

  useEffect(() => {
    fetchBrand().then(setBrand).catch(() => {});
  }, []);

  useEffect(() => {
    if (saveState !== "saved") return;
    const t = setTimeout(() => setSaveState("idle"), 2400);
    return () => clearTimeout(t);
  }, [saveState]);

  useEffect(() => {
    const prev = document.title;
    document.title = "Villa 142 — A private design reflection";
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    initQueue(
      (s) => setSaveState(s),
      () => { clearToken(); setPhase("gate"); },
      (key, contrib, latest) => setConflicts((prev) => ({ ...prev, [`${key}::${contrib}`]: { latest } }))
    );
  }, []);

  useEffect(() => { entriesRef.current = entriesMap; }, [entriesMap]);

  const steps = useMemo(() => buildSteps(def, entriesMap), [def, entriesMap]);
  const currentStep = steps[step];
  const activeStepKey = mode === "flow" && currentStep ? `${step}:${currentStep.type === "question" ? currentStep.question.id : currentStep.type}` : mode;

  // Root canvas colour follows the stage on screen (cream, or the media overlay tone).
  const stageKind =
    phase === "app" && mode === "flow" && currentStep?.type === "welcome" ? (welcomeMedia?.kind ? "media" : "paper")
    : phase === "app" && mode === "thanks" ? (endingMedia?.kind ? "media" : "paper")
    : "paper";
  useEffect(() => {
    document.documentElement.setAttribute("data-v142-stage", stageKind);
  }, [stageKind]);

  // Sticky-header height, measured from the rendered element (incl. safe-area padding) and kept
  // current with ResizeObserver. Published as --v142-header-h for scroll-margin-top and positioning.
  useEffect(() => {
    const el = headerRef.current;
    if (!el || phase !== "app") return undefined;
    const publish = () => document.documentElement.style.setProperty("--v142-header-h", `${Math.round(el.getBoundingClientRect().height)}px`);
    publish();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    window.addEventListener("resize", publish);
    return () => { ro?.disconnect(); window.removeEventListener("resize", publish); };
  }, [phase, mode]);

  // Question-transition reset, keyed to the active question. Runs only after an explicit
  // navigation (navSeq) has committed the target step's DOM — never while editing. The document is
  // the single scroll owner: reset it to 0 (the heading sits in flow below the sticky header, so 0
  // is always the correct top), re-verify on the following frames (iOS restores the old offset
  // after layout, keyboard dismissal and inertia) and on visualViewport resize; the loop is
  // frame-bounded and stops on intentional scrolling. The heading then receives focus with
  // preventScroll.
  useLayoutEffect(() => {
    if (navSeq === 0) return undefined;
    resetScrollOwner();
    navLockRef.current = false; // the target step has committed; controls accept the next tap
    let frames = 0;
    let raf = 0;
    let cancelled = false;
    const stop = () => { cancelled = true; cancelAnimationFrame(raf); };
    const opts = { passive: true };
    window.addEventListener("touchstart", stop, opts);
    window.addEventListener("wheel", stop, opts);
    const tick = () => {
      if (cancelled) return;
      if (scrollOwnerTop() !== 0) resetScrollOwner();
      if (frames === 0) {
        const heading = document.querySelector(".v142-flow-heading");
        if (heading) heading.focus({ preventScroll: true });
      }
      frames += 1;
      if (frames < 30) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const vv = window.visualViewport;
    const onViewport = () => { if (!cancelled && scrollOwnerTop() !== 0) resetScrollOwner(); };
    vv?.addEventListener("resize", onViewport);
    return () => {
      stop();
      window.removeEventListener("touchstart", stop, opts);
      window.removeEventListener("wheel", stop, opts);
      vv?.removeEventListener("resize", onViewport);
    };
  }, [navSeq, activeStepKey]);

  useEffect(() => {
    if (steps.length && step > steps.length - 1) setStep(steps.length - 1);
  }, [steps.length, step]);

  const loadAll = useCallback(async () => {
    const [qdata, pdata, wmedia, emedia] = await Promise.all([
      fetchQuestionnaire(), fetchProject(), fetchWelcomeMedia("welcome").catch(() => null), fetchWelcomeMedia("ending").catch(() => null),
    ]);
    setWelcomeMedia(wmedia);
    setEndingMedia(emedia);
    const definition = normalizeDefinition(qdata);
    const ppl = buildPeople(qdata.contributors);
    const map = {};
    pdata.entries.forEach((e) => {
      (map[e.key] = map[e.key] || {})[e.contributor] = e;
      savedRef.current.add(`${e.key}::${e.contributor}`);
    });
    setEntryRevs(pdata.entries);
    setDef(definition);
    setPeople(ppl);
    setEntriesMap(map);
    setQActive({});
    setConflicts({});
    setSubmittedAt(pdata.submitted ? pdata.shared_at || pdata.submitted_at : null);
    setUpdatedAfterShareAt(pdata.last_updated_after_share_at || null);

    const stored = localStorage.getItem(LS_RESPONDER);
    const resp = ppl.order.includes(stored) ? stored : null;
    setResponder(resp);

    const allSteps = buildSteps(definition, map);
    const lastQid = localStorage.getItem(LS_LAST_QID);
    if (resp && lastQid) {
      const idx = allSteps.findIndex((s) => s.type === "question" && s.question.id === lastQid);
      if (idx >= 0) {
        setStep(idx);
        if (localStorage.getItem(LS_RETURN_CTX) === "review") setReturnToReview(true);
      } else {
        setStep(0);
      }
    } else {
      setStep(0);
    }
    setMode("flow");
  }, []);

  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return; // StrictMode double-invokes effects; a second loadAll would wipe freshly typed state
    bootedRef.current = true;
    const boot = async () => {
      if (!getToken()) { setPhase("gate"); return; }
      try {
        await loadAll();
        setPhase("app");
      } catch {
        clearToken();
        setPhase("gate");
      }
    };
    boot();
  }, [loadAll]);

  /* ---------- remember position ---------- */

  useEffect(() => {
    if (phase !== "app" || mode !== "flow") return;
    const cur = steps[step];
    if (cur?.type === "question") localStorage.setItem(LS_LAST_QID, cur.question.id);
  }, [phase, mode, step, steps]);

  /* ---------- entry mutation ---------- */

  const patchEntry = useCallback((key, contrib, patch) => {
    setEntriesMap((prev) => {
      const byC = { ...(prev[key] || {}) };
      byC[contrib] = { key, contributor: contrib, value: {}, media: [], ...(byC[contrib] || {}), ...patch, updated_at: new Date().toISOString() };
      return { ...prev, [key]: byC };
    });
  }, []);

  const onValue = useCallback((question, contrib, value) => {
    patchEntry(question.id, contrib, { value, input_type: question.input });
    const k = `${question.id}::${contrib}`;
    if (conflicts[k]) return; // hold unsaved work until the client resolves the conflict
    const existing = entriesRef.current[question.id]?.[contrib];
    const meaningful = valueHasContent(value);
    if (meaningful || savedRef.current.has(k) || existing?.id || (existing?.media || []).length) {
      savedRef.current.add(k);
      queueEntrySave(question.id, contrib, { value, input_type: question.input, question_version: def?.version });
    }
  }, [patchEntry, def, conflicts]);

  const mergeMedia = (latest, mine) => {
    const seen = new Set((latest?.media || []).map((m) => m.id));
    return [...(latest?.media || []), ...((mine?.media || []).filter((m) => !seen.has(m.id)))];
  };

  const resolveConflict = useCallback((qid, contrib, choice) => {
    const id = `${qid}::${contrib}`;
    const latest = conflicts[id]?.latest;
    setConflicts((prev) => { const n = { ...prev }; delete n[id]; return n; });
    if (!latest) return;
    setEntryRev(qid, contrib, latest.rev);
    savedRef.current.add(id);
    const mine = entriesRef.current[qid]?.[contrib];
    if (choice === "latest") {
      setEntriesMap((prev) => ({ ...prev, [qid]: { ...(prev[qid] || {}), [contrib]: { ...latest, media: mergeMedia(latest, mine) } } }));
      return;
    }
    const q = findQuestion(def, qid);
    setEntriesMap((prev) => ({
      ...prev,
      [qid]: { ...(prev[qid] || {}), [contrib]: { ...(mine || latest), id: latest.id, media: mergeMedia(latest, mine), updated_at: new Date().toISOString() } },
    }));
    queueEntrySave(qid, contrib, { value: mine?.value || {}, input_type: q?.input || latest.input_type, question_version: def?.version });
  }, [conflicts, def]);

  const mediaOps = useMemo(() => ({
    add: (key, contrib, item) =>
      setEntriesMap((prev) => {
        const byC = { ...(prev[key] || {}) };
        const e = { key, contributor: contrib, value: {}, media: [], ...(byC[contrib] || {}) };
        byC[contrib] = { ...e, media: [...(e.media || []), item], updated_at: new Date().toISOString() };
        savedRef.current.add(`${key}::${contrib}`);
        return { ...prev, [key]: byC };
      }),
    remove: (key, contrib, id) =>
      setEntriesMap((prev) => {
        const byC = { ...(prev[key] || {}) };
        if (!byC[contrib]) return prev;
        byC[contrib] = { ...byC[contrib], media: (byC[contrib].media || []).filter((m) => m.id !== id) };
        return { ...prev, [key]: byC };
      }),
    caption: (key, contrib, id, caption) =>
      setEntriesMap((prev) => {
        const byC = { ...(prev[key] || {}) };
        if (!byC[contrib]) return prev;
        byC[contrib] = { ...byC[contrib], media: (byC[contrib].media || []).map((m) => (m.id === id ? { ...m, caption } : m)) };
        return { ...prev, [key]: byC };
      }),
  }), []);

  const deleteReflection = useCallback(async (qid, contrib) => {
    clearPending(qid, contrib);
    savedRef.current.delete(`${qid}::${contrib}`);
    setConflicts((prev) => { const n = { ...prev }; delete n[`${qid}::${contrib}`]; return n; });
    setEntriesMap((prev) => {
      const byC = { ...(prev[qid] || {}) };
      delete byC[contrib];
      return { ...prev, [qid]: byC };
    });
    // A deleted non-global perspective returns to "+ Add another perspective"; the global responder keeps their empty slot.
    if (contrib !== responder) {
      setQActive((prev) => (prev[qid] === contrib ? omitKey(prev, qid) : prev));
    }
    try { await deleteEntry(qid, contrib); } catch {}
  }, [responder]);

  /* ---------- navigation ---------- */

  const clearLocalChoice = (qid) => setQActive((prev) => (qid in prev ? omitKey(prev, qid) : prev));

  const goTo = (idx, keepReturn = false) => {
    if (!keepReturn) {
      setReturnToReview(false);
      localStorage.setItem(LS_RETURN_CTX, "flow");
    }
    const cur = steps[step];
    if (cur?.type === "question") clearLocalChoice(cur.question.id); // temporary tab choice lives only on the open question
    setStep(idx);
    setMode("flow");
    setMapOpen(false);
    setNavSeq((n) => n + 1); // triggers the layout-safe scroll reset + heading focus
  };

  const activeFor = (qid) => qActive[qid] || responder || people?.defaultId;

  const SAVE_ERROR = "We couldn't save this reflection just now — your words are kept on this device. Check your connection and try again.";
  const CONFLICT_ERROR = "This reflection was updated on another device — choose which version to keep before moving on.";

  // Commits a navigation. navLockRef stays set until the target step has committed (cleared by
  // the scroll-reset effect), so rapid repeated taps cannot navigate or save twice.
  const commitNav = (idx, { keepReturn = false, contrib = null } = {}) => {
    navLockRef.current = true;
    setNavError("");
    if (idx === null) {
      openReviewMode();
      return;
    }
    goTo(idx, keepReturn);
    if (contrib && steps[idx]?.type === "question") setQActive((prev) => ({ ...prev, [steps[idx].question.id]: contrib }));
  };

  // Immediate navigation (Back, Skip for now, resume): one tap → one outcome. Any autosave still
  // queued keeps saving in the background exactly as before.
  // The lock is a ref (not state) so a second tap landing before React re-renders is still ignored.
  const tapGuard = () => navLockRef.current;

  const navigateNow = (idx, opts) => {
    if (tapGuard()) return false;
    blurActive();
    commitNav(idx, opts);
    return true;
  };

  // Saving navigation (Continue, map, review → question, save-and-exit): the open response is
  // saved first with a visible in-progress state; a failed save keeps the client here with the
  // response intact and a clear retry message; success navigates on the same tap.
  const navigateSaving = async (idx, opts) => {
    if (tapGuard()) return false;
    blurActive();
    const cur = steps[step];
    if (mode === "flow" && cur?.type === "question") {
      const qid = cur.question.id;
      if (conflicts[`${qid}::${activeFor(qid)}`]) {
        setNavError(CONFLICT_ERROR);
        return false;
      }
    }
    if (hasPending()) {
      navLockRef.current = true;
      setNavBusy(true);
      let res;
      try {
        res = await flushNow();
      } catch {
        res = { ok: false, reason: "network" };
      }
      setNavBusy(false);
      navLockRef.current = false;
      if (res.reason === "conflict") {
        setNavError(CONFLICT_ERROR);
        return false;
      }
      if (!res.ok && res.reason !== "busy") {
        setNavError(SAVE_ERROR);
        return false;
      }
    }
    commitNav(idx, opts);
    return true;
  };

  const openReviewMode = () => {
    setReturnToReview(false);
    localStorage.setItem(LS_RETURN_CTX, "flow");
    setMode("review");
    setMapOpen(false);
    setNavSeq((n) => n + 1);
  };

  const nextIdx = () => (step >= steps.length - 1 ? null : step + 1);
  const next = () => navigateSaving(nextIdx(), { keepReturn: returnToReview });
  const skip = () => navigateNow(nextIdx(), { keepReturn: returnToReview });
  const back = () => { if (step > 0) navigateNow(step - 1, { keepReturn: returnToReview }); };

  const goToQuestion = (qid, contrib, fromReview = false) => {
    const idx = steps.findIndex((st) => st.type === "question" && st.question.id === qid);
    if (idx < 0) return;
    if (fromReview) {
      setReturnToReview(true);
      localStorage.setItem(LS_RETURN_CTX, "review");
    }
    navigateSaving(idx, { keepReturn: fromReview, contrib });
  };

  const saveAndLeave = async () => {
    if (navBusy) return;
    setMapOpen(false);
    blurActive();
    setNavBusy(true);
    let res;
    try { res = await flushNow(); } catch { res = { ok: false, reason: "network" }; }
    setNavBusy(false);
    if (!res.ok && res.reason !== "conflict" && res.reason !== "busy") {
      setNavError(SAVE_ERROR);
      return;
    }
    setNavError("");
    setMode("paused");
    setNavSeq((n) => n + 1);
  };

  const resume = () => {
    setMode("flow");
    setNavSeq((n) => n + 1);
  };

  const pickResponder = async (id) => {
    const cur = steps[step];
    if (cur?.type === "question") {
      const qid = cur.question.id;
      const active = activeFor(qid);
      if (conflicts[`${qid}::${active}`]) return { ok: false, reason: "conflict" };
      if (hasPendingFor(qid, active)) {
        const res = await flushNow(); // meaningful unsaved work is saved before the switch
        if (!res.ok) return res;
      }
      clearLocalChoice(qid);
    }
    setResponder(id);
    localStorage.setItem(LS_RESPONDER, id);
    savePrefs({ contributor: id });
    return { ok: true };
  };

  const chooseResponder = (id) => {
    setResponder(id);
    localStorage.setItem(LS_RESPONDER, id);
    savePrefs({ contributor: id });
  };

  const firstQuestionIdx = steps.findIndex((s) => s.type === "question");

  const continueFrom = (cid) => {
    let idx = steps.findIndex((s) => s.type === "question" && !hasContent(entriesMap[s.question.id]?.[cid]));
    if (idx < 0) idx = steps.findIndex((s) => s.type === "question" && !Object.values(entriesMap[s.question.id] || {}).some((e) => hasContent(e)));
    if (idx < 0) idx = firstQuestionIdx;
    navigateNow(Math.max(idx, 0));
  };

  const chooseWhere = () => {
    if (firstQuestionIdx >= 0) setStep(firstQuestionIdx);
    setMapOpen(true);
  };

  const submit = async () => {
    if (submitting) return;
    const firstShare = !submittedAt;
    setSubmitting(true);
    setSubmitError("");
    try {
      await flushNow();
      const res = await submitReflections();
      setSubmittedAt(res.shared_at || res.submitted_at);
      setUpdatedAfterShareAt(res.last_updated_after_share_at || null);
      if (firstShare) {
        setMode("thanks");
        setNavSeq((n) => n + 1);
      }
    } catch {
      setSubmitError("Something went wrong while sharing. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGateSuccess = async () => {
    try {
      await loadAll();
      setPhase("app");
    } catch {
      setPhase("gate");
    }
  };

  /* ---------- render ---------- */

  if (phase === "checking" || (phase === "app" && (!def || !people))) {
    return (
      <div className="v142 v142-stage v142-loading" data-testid="v142-loading">
        <span className="v142-eyebrow">Villa 142</span>
      </div>
    );
  }

  if (phase === "gate") return <Gate brand={brand} onSuccess={handleGateSuccess} />;

  const current = steps[step];

  if (mode === "flow" && current?.type === "welcome") {
    return <Welcome media={welcomeMedia} onBegin={next} />;
  }

  const sinceMark = updatedAfterShareAt && updatedAfterShareAt > submittedAt ? updatedAfterShareAt : submittedAt;
  const changedSince = Boolean(
    submittedAt &&
    Object.values(entriesMap).some((byC) =>
      Object.values(byC).some((e) => hasContent(e) && e.updated_at && e.updated_at > sinceMark)
    )
  );

  let loc = null;
  if (mode === "review") {
    const totals = def.chapters.reduce(
      (acc, ch) => {
        const { answered, total } = chapterCounts(ch, entriesMap);
        return { a: acc.a + answered, t: acc.t + total };
      },
      { a: 0, t: 0 }
    );
    loc = { label: "Review and Share", pct: totals.t ? (totals.a / totals.t) * 100 : 0, aria: "Review and Share — open your reflections" };
  } else if (mode === "flow" && current && (current.type === "question" || current.type === "pause" || current.type === "chapter_end")) {
    const ch = def.chapters[current.chapter];
    const { answered, total } = chapterCounts(ch, entriesMap);
    if (current.type === "question") {
      const visible = ch.questions.filter((q) => questionVisible(q, entriesMap));
      const pos = visible.findIndex((q) => q.id === current.question.id) + 1;
      loc = {
        label: `${ch.title} · ${pos} of ${total}`,
        pct: total ? (pos / total) * 100 : 0,
        aria: `${ch.title}, question ${pos} of ${total}. Open reflection map.`,
      };
    } else {
      loc = { label: ch.title, pct: total ? (answered / total) * 100 : 0, aria: `${ch.title} — open your reflections` };
    }
  }

  const nextChapterTitle = (ci) => {
    for (let i = ci + 1; i < def.chapters.length; i += 1) {
      const qs = def.chapters[i].questions.filter((q) => questionVisible(q, entriesMap));
      if (qs.length) return def.chapters[i].title;
    }
    return "Review and Share";
  };

  const currentQid = mode === "flow" && current?.type === "question" ? current.question.id : null;

  const personNames = people.list.filter((c) => !c.is_shared).map((c) => c.name);
  const namesLine = personNames.length
    ? personNames.length > 1
      ? `${personNames.slice(0, -1).join(", ")} and ${personNames[personNames.length - 1]}`
      : personNames[0]
    : "";

  if (mode === "thanks") {
    return <EndingScreen media={endingMedia} names={namesLine} onReturn={openReviewMode} />;
  }

  const renderQuestionStep = (stepDef) => {
    const q = stepDef.question;
    return (
      <>
        <QuestionCard
          question={q}
          entriesMap={entriesMap}
          active={activeFor(q.id)}
          responder={responder}
          people={people}
          isFirst={step === firstQuestionIdx}
          conflict={conflicts[`${q.id}::${activeFor(q.id)}`] || null}
          onResolveConflict={resolveConflict}
          onSetActive={(qid, c) => setQActive((prev) => ({ ...prev, [qid]: c }))}
          onValue={onValue}
          onDeleteReflection={deleteReflection}
          mediaOps={mediaOps}
        />
        {navError && (
          <p className="v142-nav-error" role="alert" data-testid="v142-nav-error">{navError}</p>
        )}
        <FlowNav
          onBack={back}
          onSkip={skip}
          onContinue={next}
          canBack={step > 0}
          showSkip={!Object.values(entriesMap[q.id] || {}).some((e) => hasContent(e))}
          busy={navBusy}
        />
      </>
    );
  };

  return (
    <div className="v142" data-testid="v142-app">
      <header className="v142-header" ref={headerRef}>
        <span className="v142-header-mark">Villa 142</span>
        {loc && (
          <button type="button" className="v142-loc" data-testid="v142-location-btn" onClick={() => setMapOpen(true)} aria-haspopup="dialog" aria-label={loc.aria}>
            <span className="v142-loc-top">
              <span className="v142-loc-label">{loc.label}</span>
              <span className="v142-chev" aria-hidden="true">▾</span>
            </span>
            <span className="v142-loc-track" aria-hidden="true">
              <span className="v142-loc-fill" style={{ width: `${loc.pct}%` }} />
            </span>
          </button>
        )}
        <nav className="v142-header-links">
          <SavedIndicator state={saveState} />
          {returnToReview && mode === "flow" && (
            <button className="v142-link" data-testid="v142-back-to-review-btn" onClick={openReviewMode}>
              Back to review
            </button>
          )}
          {mode === "review" && (
            <button className="v142-link" data-testid="v142-back-to-questions-btn" onClick={() => setMode("flow")}>
              Back to the reflection
            </button>
          )}
          {responder && <ResponderPicker people={people} responder={responder} onPick={pickResponder} />}
        </nav>
      </header>

      <main className="v142-main">
        {mode === "paused" && <PausedScreen onResume={resume} />}
        {mode === "review" && (
          <div className="v142-step" key="review">
            <Review
              def={def}
              entriesMap={entriesMap}
              people={people}
              onEdit={(qid, c) => goToQuestion(qid, c, true)}
              onSubmit={submit}
              submitting={submitting}
              error={submitError}
              submitted={Boolean(submittedAt)}
              submittedAt={submittedAt}
              updatedAfterShareAt={updatedAfterShareAt}
              changedSince={changedSince}
            />
          </div>
        )}
        {mode === "flow" && current && (
          <div className="v142-step" key={`step-${step}-${current.type === "question" ? current.question.id : current.type}`}>
            {current.type === "welcome" && <Welcome media={welcomeMedia} onBegin={next} />}
            {current.type === "contributor" && (
              <ContributorChoice
                people={people}
                current={responder}
                onChoose={chooseResponder}
                onContinueFrom={continueFrom}
                onChooseWhere={chooseWhere}
              />
            )}
            {current.type === "pause" && (
              <ChapterPause chapter={def.chapters[current.chapter]} chapterIndex={current.chapter} onContinue={next} />
            )}
            {current.type === "question" && renderQuestionStep(current)}
            {current.type === "chapter_end" && (
              <ChapterEnd
                chapter={def.chapters[current.chapter]}
                nextTitle={nextChapterTitle(current.chapter)}
                onContinue={next}
                onChoose={() => setMapOpen(true)}
                onSaveLeave={saveAndLeave}
              />
            )}
          </div>
        )}
      </main>

      <ReflectionMap
        open={mapOpen}
        def={def}
        entriesMap={entriesMap}
        people={people}
        pendingSet={pendingQuestionKeys()}
        currentQid={currentQid}
        onNavigate={(qid) => goToQuestion(qid)}
        onReview={openReviewMode}
        onSaveLeave={saveAndLeave}
        onClose={() => setMapOpen(false)}
      />
    </div>
  );
}
